import { ROUTER_PATH } from "@config/router";

/**
 * deeplink 首段（host 或 go.link 首段 path）到业务路由的映射：
 * - edgen://member?... -> /member?...
 * - https://edwealth.go.link/member?... -> /member?...
 */
const DEEP_LINK_ROUTE_MAP: Record<string, string> = {
  member: ROUTER_PATH.MEMBER,
  alphapick: ROUTER_PATH.ALPHA_PICK,
};

/** Adjust 品牌 / 长链域名（pathname 第一段为业务段，如 /member） */
const ADJUST_GO_LINK_HOSTS = new Set([
  "edwealth.go.link",
  "www.edwealth.go.link",
  // 历史域名，保留兼容存量链接
  "edgen.go.link",
  "www.edgen.go.link",
  "wbsy.adj.st",
  "www.wbsy.adj.st",
]);

/** Adjust 控制类 query（`adj_*`），不进站内路由 */
function isStrippedQueryKey(key: string): boolean {
  return key.toLowerCase().startsWith("adj_");
}

/** 去掉 Adjust 控制参数，保留业务 query（可有可无） */
function buildBusinessQueryString(parsed: URL): string {
  const out = new URLSearchParams();
  parsed.searchParams.forEach((value, key) => {
    if (isStrippedQueryKey(key)) {
      return;
    }
    out.append(key, value);
  });
  const s = out.toString();
  return s ? `?${s}` : "";
}

/**
 * 将 path 首段映射到站内基路径，其余 path 原样拼接：
 * `/member` -> `/member`，`/member/foo` -> `/member/foo`（若首段在 map 中则用映射基路径）
 */
function mapPathFirstSegmentToRoute(pathname: string): string {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const segments = normalized.split("/").filter(Boolean);
  if (segments.length === 0) {
    return "/";
  }
  const first = segments[0].toLowerCase();
  const base = (DEEP_LINK_ROUTE_MAP[first] ?? `/${first}`).replace(/\/+$/, "");
  const rest = segments.slice(1);
  if (rest.length === 0) {
    // 裸 alphapick 是产品旧根，落到 Invest Opportunity，不是营销页
    if (first === "alphapick") {
      return ROUTER_PATH.INVEST_OPPORTUNITY;
    }
    return base || "/";
  }
  return `${base}/${rest.join("/")}`;
}

/**
 * 将原生上报的 URL 映射为 Web 内部路由（仅两种正式形态 + 兜底）：
 *
 * 1. **App Scheme**：`edgen://member?tab=pricing`（业务 query 可选）
 * 2. **Adjust 长链**：`https://edwealth.go.link/member?...` 或历史 `edgen.go.link` / `wbsy.adj.st`（pathname 第一段为业务段）
 * 3. **兜底**：其它 `http(s)://` 等 → `pathname` + 业务 query + hash（仍过滤 `adj_*`）
 */
export function mapDeepLinkToRoute(rawUrl: string): string | undefined {
  if (!rawUrl) {
    return undefined;
  }

  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return undefined;
  }

  const scheme = parsed.protocol.replace(":", "").toLowerCase();
  const host = parsed.hostname.toLowerCase();

  // 1) edgen://<host>/path?...
  if (scheme === "edgen" && host) {
    const pathSuffix =
      parsed.pathname && parsed.pathname !== "/" ? parsed.pathname : "";
    const combinedPath = `/${host}${pathSuffix}`;
    const routePath = mapPathFirstSegmentToRoute(combinedPath);
    return `${routePath}${buildBusinessQueryString(parsed)}${parsed.hash}`;
  }

  // 2) https://edwealth.go.link/member?...
  if (
    (scheme === "https" || scheme === "http") &&
    ADJUST_GO_LINK_HOSTS.has(host)
  ) {
    const routePath = mapPathFirstSegmentToRoute(parsed.pathname || "/");
    return `${routePath}${buildBusinessQueryString(parsed)}${parsed.hash}`;
  }

  // 3) 兜底
  let routePath = parsed.pathname || "/";
  if (!routePath.startsWith("/")) {
    routePath = `/${routePath}`;
  }
  return `${routePath}${buildBusinessQueryString(parsed)}${parsed.hash}`;
}
